/* ===========================================================
   Blog / Recursos — índice (/blog) e post de artigo (/blog/:slug).
   O índice é o hub de TODO o conteúdo apresentado como post: guia em
   destaque + artigos + conceitos (glossário) + seção de ferramentas
   (calculadoras). Conteúdo em content/artigos.ts e content/glossario.ts.
   =========================================================== */
import { Link, Navigate } from "react-router-dom";
import { useSeo } from "../../seo/use-seo";
import { titulo } from "../../seo/site";
import { JsonLd, breadcrumbLd, articleLd } from "../../seo/jsonld";
import { ARTIGOS, acharArtigo, tempoLeituraMin } from "../../content/artigos";
import { VERBETES, acharVerbete, tempoLeituraSecoes } from "../../content/glossario";
import { LucraaMark, Icon } from "../../components/ui.jsx";
import { useContentTracking } from "../../analytics/content-tracking";
import {
  ConteudoNav,
  ConteudoShell,
  Breadcrumb,
  Secoes,
  PostHeader,
  PostRelated,
  CtaDemo,
  NewsletterForm,
  coverInfo,
} from "./shell";
import { SiteFooter } from "../../components/site-footer";

const HOME = { nome: "Início", path: "/" };
const BLOG = { nome: "Blog", path: "/blog" };

interface FeedItem {
  path: string;
  categoria: string;
  titulo: string;
  resumo: string;
  tempoMin: number;
}

const GUIA: FeedItem = {
  path: "/lucro-real-ecommerce",
  categoria: "Guia",
  titulo: "Lucro real no e-commerce: o guia completo",
  resumo:
    "O guia que conecta ROAS, margem de contribuição, CMV e lucro das vendas — do custo do produto à decisão de escalar ou pausar uma campanha.",
  tempoMin: 6,
};

const FERRAMENTAS = [
  {
    path: "/calculadora/roas-de-equilibrio",
    nome: "Calculadora de ROAS de equilíbrio",
    desc: "O ROAS mínimo para a campanha parar de dar prejuízo.",
  },
  {
    path: "/calculadora/margem-de-contribuicao",
    nome: "Calculadora de margem de contribuição",
    desc: "Quanto cada venda deixa antes da mídia e dos custos fixos.",
  },
];

function buildFeed(): FeedItem[] {
  const artigos: FeedItem[] = ARTIGOS.map((a) => ({
    path: `/blog/${a.slug}`,
    categoria: a.categoria,
    titulo: a.titulo,
    resumo: a.resumo,
    tempoMin: tempoLeituraMin(a),
  }));
  const conceitos: FeedItem[] = VERBETES.map((v) => ({
    path: `/glossario/${v.slug}`,
    categoria: "Conceitos",
    titulo: v.pergunta,
    resumo: v.resumo,
    tempoMin: tempoLeituraSecoes(v.resumo, v.secoes),
  }));
  return [...artigos, ...conceitos];
}

function FeedCard({ item, destaque = false }: { item: FeedItem; destaque?: boolean }) {
  const { grad, icon } = coverInfo(item.categoria);
  return (
    <Link to={item.path} className={"blog-card" + (destaque ? " blog-card--featured" : "")}>
      <div className="blog-cover" style={{ background: grad }}>
        <span className="blog-cover-ico" aria-hidden="true">
          <Icon name={icon} size={destaque ? 150 : 108} color="rgba(255,255,255,0.22)" />
        </span>
        <span className="blog-cat">{item.categoria}</span>
      </div>
      <div className="blog-card-body">
        <h2 className="blog-card-title">{item.titulo}</h2>
        <p className="blog-card-excerpt">{item.resumo}</p>
        <div className="blog-meta">
          <span className="blog-avatar" aria-hidden="true">
            <LucraaMark size={13} tone="#5fe3a1" />
          </span>
          <span>Time Lucraa+</span>
          <span className="dot" aria-hidden="true" />
          <span>{item.tempoMin} min de leitura</span>
        </div>
      </div>
    </Link>
  );
}

export function BlogIndex() {
  useSeo({
    title: titulo("Blog — lucro, ROAS e tráfego pago para lojas Nuvemshop"),
    description:
      "Guias, conceitos e ferramentas sobre lucro real, ROAS de equilíbrio e gestão de campanhas para lojistas Nuvemshop que investem em tráfego pago.",
    path: "/blog",
  });
  useContentTracking({ tipo: "blog", titulo: "Blog" });
  const feed = buildFeed();
  return (
    <div className="lp-root">
      <div className="lp-hero-bg" style={{ minHeight: "100vh" }}>
        <ConteudoNav />
        <main
          className="lp-wrap"
          aria-labelledby="conteudo-h1"
          style={{ paddingTop: 24, paddingBottom: 80 }}
        >
          <Breadcrumb itens={[HOME, BLOG]} />
          <JsonLd data={breadcrumbLd([HOME, BLOG])} />
          <header className="blog-head">
            <span className="c-eyebrow">Recursos</span>
            <h1 id="conteudo-h1">Lucro, ROAS e tráfego pago — sem achismo</h1>
            <p className="blog-lead">
              Guias, conceitos e ferramentas para transformar ROAS em lucro de verdade. Conteúdo
              direto para lojistas Nuvemshop que investem em mídia.
            </p>
          </header>

          <FeedCard item={GUIA} destaque />

          <div className="blog-grid" style={{ marginTop: 24 }}>
            {feed.map((it) => (
              <FeedCard key={it.path} item={it} />
            ))}
          </div>

          <section aria-label="Ferramentas" style={{ marginTop: 52 }}>
            <h2 className="blog-section-title">Ferramentas gratuitas</h2>
            <div className="ferramentas-grid">
              {FERRAMENTAS.map((f) => (
                <Link key={f.path} to={f.path} className="ferramenta-card">
                  <span className="ferramenta-ico" aria-hidden="true">
                    <Icon name="sliders" size={20} color="var(--mint)" />
                  </span>
                  <span className="ferramenta-txt">
                    <strong>{f.nome}</strong>
                    <span>{f.desc}</span>
                  </span>
                  <span className="rc-arrow" aria-hidden="true">
                    →
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <NewsletterForm />
          <CtaDemo />
        </main>
        <SiteFooter />
      </div>
    </div>
  );
}

export function Artigo({ slug }: { slug: string | undefined }) {
  const a = acharArtigo(slug);
  // useSeo antes do early-return (regras de hooks). Em rota válida a existe sempre.
  useSeo({
    title: a ? titulo(a.tituloSeo) : titulo("Blog"),
    description: a ? a.resumo : "Blog do Lucraa+ sobre lucro, ROAS e tráfego pago.",
    path: a ? `/blog/${a.slug}` : "/blog",
    ogType: "article",
  });
  useContentTracking({ tipo: "artigo", categoria: a?.categoria, titulo: a?.titulo });
  if (!a) return <Navigate to="/blog" replace />;
  const path = `/blog/${a.slug}`;
  const relacionados = a.verbetes
    .map((s) => {
      const v = acharVerbete(s);
      return v ? { nome: v.termo, path: `/glossario/${v.slug}` } : null;
    })
    .filter((x): x is { nome: string; path: string } => x !== null);

  return (
    <ConteudoShell breadcrumb={[HOME, BLOG, { nome: a.titulo, path }]}>
      <JsonLd data={breadcrumbLd([HOME, BLOG, { nome: a.titulo, path }])} />
      <JsonLd
        data={articleLd({
          titulo: a.titulo,
          descricao: a.resumo,
          path,
          publicadoEm: a.publicadoEm,
          atualizadoEm: a.atualizadoEm,
        })}
      />
      <PostHeader
        categoria={a.categoria}
        titulo={a.titulo}
        dek={a.resumo}
        publicadoEm={a.publicadoEm}
        tempoMin={tempoLeituraMin(a)}
      />
      <div className="article-body">
        <Secoes secoes={a.secoes} />
      </div>
      <PostRelated titulo="No glossário" itens={relacionados} />
      <NewsletterForm />
      <CtaDemo />
    </ConteudoShell>
  );
}
